<template>
  <div class="w-full">
    <StoreFinderMap
      class="hidden md:inline-block md:h-[300px]"
      :stores="stores"
    />
    <div class="mx-auto flex w-full max-w-7xl flex-col">
      <StoreDetailSkeleton v-if="isLoading" />
      <template v-else>
        <!-- @todo breadcrumbs -->
        <div class="flex flex-col items-center px-6 py-8 text-center md:py-16">
          <Headline class="mb-7" :tag="headlineTag" :look="headlineLook">
            {{ headline }}
          </Headline>
          <RichTextRenderer v-if="content" :document="content" />
          <div
            v-else
            v-dompurify-html="
              $t('store_finder.city_page.content', { city: cityName })
            "
            class="max-w-4xl"
          ></div>
        </div>

        <div
          class="grid gap-4 px-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4"
        >
          <StoreFinderCityStore
            v-for="store in cityStores"
            :key="store.id"
            :store="store"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  generateBreadcrumbSchema,
  getUrl,
  metaTagGenerator,
  type IBreadcrumbItem,
} from '~/utils/seo'
import { useGeocode } from '~/composables/useGeocode'
import { useStores } from '~/composables/useStores'
import type { IStore, StoresParams } from '~/rpcMethods/stores'
import { getQueryParam, getRouteParam } from '~/utils/route'
import type { SbNdlCity } from '~/storyblok/types/storyblok'
import { getCmsMeta, type CmsMeta } from '~/utils/cms'

const { $currentShop } = useNuxtApp()
const { logger } = useLogging()
const $i18n = useI18n()
const route = useRoute()
const fimLocalePath = useFimLocalePath()
const services = getQueryParam(route?.query, 'services', 'ALL')

const city = getRouteParam(route.params, 'city')
const county = getRouteParam(route.params, 'county')

const {
  data: geocodeData,
  fetching: fetchingGeocode,
  fetch: fetchGeocode,
} = useGeocode()
const {
  fetch: fetchStores,
  fetching: fetchingStores,
  stores,
} = useStores(`useStores-${county}-${city}-${services}-${$currentShop.locale}`)

const { data: cityData } = await useCmsStory<SbNdlCity>({
  params: {
    slug: city,
    baseFolder: CmsBaseFolder.NDL,
  },
  options: {
    immediate: true,
  },
  key: `ndl-city-${city}`,
})

const cmsMeta = computed<CmsMeta>(() => {
  if (cityData.value && cityData.value?.content) {
    return getCmsMeta(cityData.value?.content)
  }
  return {}
})

try {
  await fetchGeocode({ search: city })
  if (geocodeData.value?.results?.length) {
    const searchParams: StoresParams = {
      lat: geocodeData.value?.results[0]?.geometry?.location
        ?.lat as unknown as number,
      lng: geocodeData.value?.results[0]?.geometry?.location
        ?.lng as unknown as number,
    }

    if (services && services !== 'ALL') {
      searchParams.type = services
    }

    await fetchStores(searchParams)
  }
} catch (error) {
  logger.error(error, {
    why: 'getCityStores',
    where: '[city].vue',
    city,
  })
}

const cityName = computed(() => {
  return geocodeData.value?.results[0]?.address_components?.find((component) =>
    component.types.includes('locality'),
  )?.long_name
})

const metaTitle = computed(() => {
  if (cmsMeta?.value && cmsMeta?.value.title) {
    return cmsMeta.value.title
  }
  return $i18n.t('store_finder.city_page.title', { city: cityName?.value })
})

const metaDescription = computed(() => {
  if (cmsMeta?.value) {
    const cmsMetaDescription = cmsMeta.value.meta?.find(
      (meta) => meta.name === 'description',
    )
    if (cmsMetaDescription?.content) {
      return cmsMetaDescription.content
    }
    const descText = $i18n.t('store_finder.city_page.seo_description', {
      city: cityName?.value,
    })
    if (!metaTitle.value) {
      return descText
    }

    return metaTitle.value + ': ' + descText
  }
  return undefined
})

const metaTags = metaTagGenerator({
  description: metaDescription.value,
  robots: 'index,follow',
})

useHead({
  link: [
    {
      rel: 'canonical',
      key: 'canonical',
      href: sanitizeCanonical(getUrl($currentShop.domain, route?.path)),
    },
  ],
})

useSeoMeta({
  ...(metaTitle.value ? { title: metaTitle.value } : {}),
  ...metaTags,
})

const cityStores = computed(() => {
  return (stores.value ?? []).filter(
    (store: IStore) =>
      cityName.value?.localeCompare(store.city, 'en', {
        sensitivity: 'base',
      }) === 0,
  )
})

const isLoading = computed(() => fetchingStores.value || fetchingGeocode.value)

const headline = computed(
  () =>
    cityData.value?.content.headline ||
    $i18n.t('store_finder.city_page.title', { city: cityName?.value }),
)

const headlineTag = computed(() =>
  toHeadlineTag(cityData.value?.content?.headline_tag, HeadlineTag.H1),
)

const headlineLook = computed(() =>
  toHeadlineLook(cityData.value?.content?.headline_look),
)

const content = computed(() => cityData.value?.content?.content)

const breadcrumbSchema = computed((): IBreadcrumbItem[] => {
  return [
    {
      name: $i18n.t('store_finder.stores'),
      item: getUrl(
        $currentShop.domain,
        fimLocalePath({ name: routeList.stores.name }),
      ),
    },
    {
      name: metaTitle.value,
      item: getUrl($currentShop.domain, route.path),
    },
  ]
})

useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))

defineOptions({ name: 'NiederlassungenCityPage' })
definePageMeta({ pageType: 'store' })
</script>
